// SPDX-License-Identifier: AGPL-3.0-or-later
// Group expenses: who paid, how it splits, who owes whom, and the fewest
// transfers that settle it.
import { splitByWeight, splitEvenly } from "./money.js";
/** Each member's share of `amount`, adding up to it exactly (for exact, as entered). */
export function splitAmount(amount, rule) {
    if (rule.kind === "equal") {
        const parts = splitEvenly(amount, rule.members.length);
        return new Map(rule.members.map((m, i) => [m, parts[i]]));
    }
    const entries = Object.entries(rule.parts).filter(([, v]) => v > 0);
    if (rule.kind === "exact")
        return new Map(entries.map(([m, v]) => [m, Math.round(v)]));
    const parts = splitByWeight(amount, entries.map(([, v]) => v));
    return new Map(entries.map(([m], i) => [m, parts[i]]));
}
/** Whether a split rule covers the amount: percentages add to 100, exact amounts to the total, and someone takes part. */
export function splitProblem(amount, rule) {
    if (rule.kind === "equal")
        return rule.members.length === 0 ? "nobody" : null;
    const values = Object.values(rule.parts).filter((v) => v > 0);
    if (values.length === 0)
        return "nobody";
    const total = values.reduce((s, v) => s + v, 0);
    if (rule.kind === "percent" && Math.abs(total - 100) > 0.001)
        return "percent";
    if (rule.kind === "exact" && Math.round(total) !== amount)
        return "exact";
    return null;
}
/**
 * Each member's balance: positive means the group owes them, negative that
 * they owe the group. The balances always add up to zero.
 */
export function groupBalances(members, spends, repayments) {
    const net = new Map(members.map((m) => [m, 0]));
    const add = (m, v) => net.set(m, (net.get(m) ?? 0) + v);
    for (const s of spends) {
        add(s.paidBy, s.amount);
        for (const [m, share] of s.shares)
            add(m, -share);
    }
    for (const r of repayments) {
        add(r.from, r.amount);
        add(r.to, -r.amount);
    }
    return net;
}
/**
 * Transfers that settle every balance. Exact matches (someone owes exactly
 * what someone else is owed) go first, then the largest debt pays the
 * largest credit, which settles a group in at most one transfer fewer than
 * its members and usually far fewer.
 */
export function settleUp(balances) {
    const owe = [...balances].filter(([, v]) => v < 0).map(([m, v]) => ({ m, v: -v }));
    const owed = [...balances].filter(([, v]) => v > 0).map(([m, v]) => ({ m, v }));
    const out = [];
    for (const d of owe) {
        const c = owed.find((c) => c.v === d.v);
        if (!c)
            continue;
        out.push({ from: d.m, to: c.m, amount: d.v });
        c.v = 0;
        d.v = 0;
    }
    for (;;) {
        const d = owe.filter((x) => x.v > 0).sort((a, b) => b.v - a.v || a.m.localeCompare(b.m))[0];
        const c = owed.filter((x) => x.v > 0).sort((a, b) => b.v - a.v || a.m.localeCompare(b.m))[0];
        if (!d || !c)
            break;
        const amount = Math.min(d.v, c.v);
        out.push({ from: d.m, to: c.m, amount });
        d.v -= amount;
        c.v -= amount;
    }
    return out;
}
//# sourceMappingURL=groups.js.map